import { ROUTES } from "./index";

export const SEO_SITE_NAME = "YourQcard";

export const SEO_DEFAULT_TITLE =
  "YourQcard – Digital ID Cards with Secure QR and Company Management";

export const SEO_DEFAULT_DESCRIPTION =
  "Create and manage professional digital ID cards with secure QR verification. Companies can buy licenses, add users, and manage employees with admin controls.";

/** Production marketing site (used for sitemap and default OG URLs). */
export const SEO_SITE_ORIGIN = "https://yourqcard.com";

export const SEO_DEFAULT_OG_IMAGE = `${SEO_SITE_ORIGIN}/logo.png`;

export const SEO_PUBLISHER = {
  name: "Integrated Digital Systems (IDS)",
  url: "https://www.ids.com.lb",
} as const;

export interface PageSeoConfig {
  title: string;
  description: string;
  /** When true, use `title` as the full document title (no " | YourQcard" suffix). */
  fullTitle?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const homeJsonLd: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SEO_SITE_NAME,
    url: SEO_SITE_ORIGIN,
    description: SEO_DEFAULT_DESCRIPTION,
    publisher: {
      "@type": "Organization",
      name: SEO_PUBLISHER.name,
      url: SEO_PUBLISHER.url,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SEO_SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: SEO_DEFAULT_DESCRIPTION,
    url: SEO_SITE_ORIGIN,
    publisher: {
      "@type": "Organization",
      name: SEO_PUBLISHER.name,
      url: SEO_PUBLISHER.url,
    },
  },
];

/** Indexable marketing and legal pages. */
export const PAGE_SEO: Record<string, PageSeoConfig> = {
  [ROUTES.HOME]: {
    title: SEO_DEFAULT_TITLE,
    description: SEO_DEFAULT_DESCRIPTION,
    fullTitle: true,
    jsonLd: homeJsonLd,
  },
  [ROUTES.ABOUT]: {
    title: "About",
    description:
      "YourQcard by IDS helps individuals, professionals, and organizations create, manage, and share secure digital identities through one modern platform.",
  },
  [ROUTES.FEATURES]: {
    title: "Features",
    description:
      "Create, manage, share, and verify professional digital identities for individuals, teams, and organizations from one secure platform.",
  },
  [ROUTES.DEMO]: {
    title: "Demo",
    description:
      "Watch a guided product tour and explore a live digital profile to see how YourQcard helps create, manage, and share professional digital identities.",
  },
  [ROUTES.PRICING]: {
    title: "Pricing",
    description:
      "Choose a company or individual package for YourQcard. Upgrade anytime with no hidden fees.",
  },
  [ROUTES.CONTACT]: {
    title: "Contact",
    description:
      "Questions, feedback, or partnership ideas? Contact the YourQcard team — we typically reply within one business day.",
  },
  [ROUTES.FAQ]: {
    title: "FAQ",
    description:
      "Search common questions about YourQcard setup, features, pricing, and security.",
  },
  [ROUTES.PRIVACY]: {
    title: "Privacy Policy",
    description: "Read how YourQcard collects, uses, and protects your personal information.",
  },
  [ROUTES.DATA_POLICY]: {
    title: "Data Policy",
    description:
      "Learn how YourQcard handles data retention, analytics, and privacy for digital identity profiles.",
  },
  [ROUTES.TERMS]: {
    title: "Terms of Service",
    description: "Terms and conditions for using the YourQcard digital identity platform.",
  },
};

/** Paths included in sitemap.xml (relative, no trailing slash except home). */
export const SITEMAP_PATHS = Object.keys(PAGE_SEO);
